<template>
  <div class="organization-products">
    <!-- Banner adaptado do OrganizationProfileHeader -->
    <organization-shop-header :organization="organization" />

    <!-- Seção de coleções -->
    <v-container>
      <h2 class="text-h4 font-weight-bold mb-6 mt-8">Coleções</h2>
      
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      
      <div v-else-if="collections.length === 0" class="text-center py-8">
        <p class="text-subtitle-1">Nenhuma coleção disponível no momento.</p>
      </div>
      
      <div v-else>
        <div v-for="(collection, index) in collections" :key="collection.id" class="mb-2">
          <div class="d-flex align-center justify-space-between mb-4">
            <h3 class="text-h5 font-weight-medium">{{ collection.name }}</h3>
            <v-btn 
              text 
              color="primary" 
              @click="goToCollection(collection.id)"
              class="text-none"
            >
              Ver todos <v-icon small right>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          
          <product-carousel :products="collection.products" @product-click="openProductModal" />
        </div>
      </div>
    </v-container>

    <!-- Modal de produto -->
    <product-detail-modal 
      v-if="selectedProduct" 
      :product="selectedProduct" 
      :show="productModalOpen"
      @close="closeProductModal" 
    />
  </div>
</template>

<script>
import OrganizationShopHeader from '@/components/shop/organization/OrganizationShopHeader.vue';
import ProductCarousel from '@/components/shop/products/ProductCarousel.vue';
import ProductDetailModal from '@/components/shop/products/ProductDetailModal.vue';

export default {
  name: 'OrganizationProducts',
  components: {
    OrganizationShopHeader,
    ProductCarousel,
    ProductDetailModal
  },
  data() {
    return {
      organization: {},
      collections: [],
      loading: true,
      selectedProduct: null,
      productModalOpen: false
    };
  },
  async created() {
    try {
      await this.fetchOrganizationData();
      await this.fetchCollections();
    } catch (error) {
      console.error('Erro ao carregar dados da loja:', error);
      this.$toast.error('Não foi possível carregar os dados da loja.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchOrganizationData() {
      // Chamada para API buscando dados da organização pelo slug
      const orgSlug = this.$route.params.orgSlug;
      
      // Simulando delay de rede
      await new Promise(resolve => setTimeout(resolve, 700));
      
      // Mockup de dados para exemplo
      this.organization = {
        id: '1',
        name: orgSlug === 'musicfest' ? 'Music Festival Official Store' : 
              orgSlug === 'sportsclub' ? 'Sports Club Fan Shop' : 
              orgSlug === 'techconf' ? 'Tech Conference Store' : 
              'Loja ' + orgSlug,
        slug: orgSlug,
        photo: `https://placehold.co/150`,
        cover: `https://placehold.co/1200x400`,
        email: `contato@${orgSlug}.com`,
        phone: '5511999999999',
        ddi: '55',
        description: 'Loja oficial com produtos exclusivos e edições limitadas. Encontre itens que não estão disponíveis em nenhum outro lugar!',
        social: {
          instagram: `@${orgSlug}`,
          facebook: `fb.com/${orgSlug}`,
          twitter: `@${orgSlug}`
        }
      };
    },
    async fetchCollections() {
      // Simulando delay de rede
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const orgType = this.$route.params.orgSlug;
      let collections = [];
      
      // Personaliza coleções com base no tipo de organização
      if (orgType === 'musicfest') {
        collections = [
          {
            id: '1',
            name: 'Camisetas Oficiais',
            products: this.generateMockProducts(8, 'Camiseta', 'music')
          },
          {
            id: '2',
            name: 'Acessórios do Festival',
            products: this.generateMockProducts(6, 'Acessório', 'music')
          },
          {
            id: '3',
            name: 'Itens Colecionáveis',
            products: this.generateMockProducts(4, 'Colecionável', 'music')
          },
          {
            id: '4',
            name: 'Headliners Collection',
            products: this.generateMockProducts(4, 'Headliner', 'music')
          },
          {
            id: '5',
            name: 'Edições Limitadas',
            products: this.generateMockProducts(3, 'Limitado', 'music')
          }
        ];
      } else if (orgType === 'sportsclub') {
        collections = [
          {
            id: '1',
            name: 'Uniformes Oficiais',
            products: this.generateMockProducts(6, 'Uniforme', 'sports')
          },
          {
            id: '2',
            name: 'Casual Wear',
            products: this.generateMockProducts(8, 'Casual', 'sports')
          },
          {
            id: '3',
            name: 'Acessórios Esportivos',
            products: this.generateMockProducts(6, 'Acessório', 'sports')
          },
          {
            id: '4',
            name: 'Itens de Torcedor',
            products: this.generateMockProducts(8, 'Torcedor', 'sports')
          }
        ];
      } else if (orgType === 'techconf') {
        collections = [
          {
            id: '1',
            name: 'Camisetas Tech',
            products: this.generateMockProducts(6, 'Camiseta', 'tech')
          },
          {
            id: '2',
            name: 'Acessórios Geek',
            products: this.generateMockProducts(8, 'Acessório', 'tech')
          },
          {
            id: '3',
            name: 'Gadgets',
            products: this.generateMockProducts(5, 'Gadget', 'tech')
          },
          {
            id: '4',
            name: 'Papelaria Tech',
            products: this.generateMockProducts(4, 'Papelaria', 'tech')
          }
        ];
      } else {
        collections = [
          {
            id: '1',
            name: 'Camisetas',
            products: this.generateMockProducts(6, 'Camiseta', 'general')
          },
          {
            id: '2',
            name: 'Acessórios',
            products: this.generateMockProducts(8, 'Acessório', 'general')
          },
          {
            id: '3',
            name: 'Lembranças',
            products: this.generateMockProducts(5, 'Lembrança', 'general')
          },
          {
            id: '4',
            name: 'Edição Especial',
            products: this.generateMockProducts(3, 'Especial', 'general')
          }
        ];
      }
      
      this.collections = collections;
    },
    generateMockProducts(count, prefix, theme) {
      const products = [];
      
      let names = [];
      let priceRange = [20, 100];
      
      // Configurar nomes e preços por tema e tipo
      if (theme === 'music') {
        if (prefix === 'Camiseta') {
          names = ['Band Tee', 'Tour Shirt', 'Fest Logo Tee', 'Artist Tee', 'Lineup Shirt', 'Stage Tee'];
          priceRange = [40, 90];
        } else if (prefix === 'Acessório') {
          names = ['Festival Bandana', 'Pulseira VIP', 'Eco Cup', 'Badge Set', 'Backstage Lanyard', 'Tote Bag'];
          priceRange = [15, 60];
        } else if (prefix === 'Colecionável') {
          names = ['Poster Autografado', 'Vinil Exclusivo', 'Guitarra Miniatura', 'Drum Stick Set', 'CD Edição Especial'];
          priceRange = [60, 200];
        } else if (prefix === 'Headliner') {
          names = ['Artist Hoodie', 'Signed Merch', 'Band Photo', 'Limited Edition Set', 'Exclusive Package'];
          priceRange = [80, 300];
        } else {
          names = ['VIP Box', 'Special Edition', 'Founders Kit', 'Anniversary Pack', 'Festival Memory Box'];
          priceRange = [150, 500];
        }
      } else if (theme === 'sports') {
        if (prefix === 'Uniforme') {
          names = ['Camisa Oficial', 'Shorts Time', 'Kit Completo', 'Jersey Titular', 'Camisa Reserva', 'Edição Torcedor'];
          priceRange = [80, 250];
        } else if (prefix === 'Casual') {
          names = ['Camiseta Casual', 'Polo Clube', 'Moletom Torcedor', 'Jaqueta Time', 'Agasalho Esportivo'];
          priceRange = [50, 180];
        } else if (prefix === 'Acessório') {
          names = ['Boné Time', 'Cachecol Torcida', 'Meia Oficial', 'Mochila Clube', 'Nécessaire Esportiva'];
          priceRange = [20, 80];
        } else {
          names = ['Bandeira Torcida', 'Mini Taça', 'Mascote Pelúcia', 'Caneca Clube', 'Adesivo Time'];
          priceRange = [10, 60];
        }
      } else if (theme === 'tech') {
        if (prefix === 'Camiseta') {
          names = ['Dev Tee', 'Coder Shirt', 'Tech Logo Tee', 'Programming Shirt', 'Geek T-shirt'];
          priceRange = [40, 90];
        } else if (prefix === 'Acessório') {
          names = ['Mouse Pad', 'Caneca Programador', 'Stickers Pack', 'Pins Tech', 'Organizador Cabos'];
          priceRange = [15, 70];
        } else if (prefix === 'Gadget') {
          names = ['Power Bank', 'Wireless Charger', 'USB Hub', 'LED Lamp', 'Desk Organizer'];
          priceRange = [50, 150];
        } else {
          names = ['Caderno Dev', 'Conjunto Canetas', 'Post-it Programador', 'Agenda Tech', 'Kit Escritório'];
          priceRange = [25, 80];
        }
      } else {
        // General theme
        if (prefix === 'Camiseta') {
          names = ['Camiseta Logo', 'Camiseta Básica', 'T-shirt Estampada', 'Camiseta Premium', 'Manga Longa'];
          priceRange = [30, 80];
        } else if (prefix === 'Acessório') {
          names = ['Boné Logo', 'Chaveiro Exclusivo', 'Ecobag', 'Pulseira Silicone', 'Caneca Temática'];
          priceRange = [10, 50];
        } else if (prefix === 'Lembrança') {
          names = ['Ímã Geladeira', 'Porta-copos', 'Adesivos Pack', 'Bloco Notas', 'Caneta Personalizada'];
          priceRange = [5, 30];
        } else {
          names = ['Box Edição Especial', 'Conjunto Exclusivo', 'Gift Set', 'Kit Premium', 'Kit Colecionador'];
          priceRange = [70, 200];
        }
      }
      
      for (let i = 1; i <= count; i++) {
        const nameIndex = Math.floor(Math.random() * names.length);
        const price = Math.floor(Math.random() * (priceRange[1] - priceRange[0])) + priceRange[0];
        
        products.push({
          id: `${theme}-${prefix.toLowerCase()}-${i}`,
          name: `${names[nameIndex]}${i !== 1 ? ' ' + i : ''}`,
          price: price,
          image: `https://placehold.co/300x300`,
          description: `${names[nameIndex]} original da nossa coleção ${prefix}. Item exclusivo com acabamento premium.`,
          inStock: Math.random() > 0.2,
          isNewProduct: Math.random() > 0.7,
          rating: (Math.random() * 2 + 3).toFixed(1)
        });
      }
      return products;
    },
    openProductModal(product) {
      this.selectedProduct = product;
      this.productModalOpen = true;
    },
    closeProductModal() {
      this.productModalOpen = false;
      this.selectedProduct = null;
    },
    goToCollection(collectionId) {
      this.$router.push({
        name: 'shop.organization.collection',
        params: {
          orgSlug: this.$route.params.orgSlug,
          collectionId
        }
      });
    }
  }
};
</script>